import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';
import ModalContentWrapper from 'components/ModalContentWrapper';

import type { RfidId } from 'redux-store/slices/ui/types';

import AddDevicesToGroupsSuccess from './AddDevicesToGroupsSuccess';
import AddNewRfidGroup from './AddNewRfidGroup';
import AddRfidsToGroups from './AddRfidsToGroups';

export type AddDevicesToGroupsStage = 'name' | 'rfids' | 'success';

export interface AddRfidsToGroupsModalState {
  stage: AddDevicesToGroupsStage;
  name: string;
  rfids: RfidId[];
  newGroupID?: number;
}

export interface AddRfidsToGroupsModalStageProps {
  open: boolean;
  state: AddRfidsToGroupsModalState;
  setState: Dispatch<SetStateAction<AddRfidsToGroupsModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  selectedRfids: RfidId[];
}

const StageComponents: Record<
  AddDevicesToGroupsStage,
  FC<AddRfidsToGroupsModalStageProps>
> = {
  name: AddRfidsToGroups,
  rfids: AddNewRfidGroup,
  success: AddDevicesToGroupsSuccess,
};

interface Props extends DefaultPopupProps {
  selectedRfids: RfidId[];
}

const AddDevicesToGroupsModal: FC<Props> = ({
  onClose,
  open,
  selectedRfids,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState<AddRfidsToGroupsModalState>({
    stage: 'name',
    name: '',
    rfids: [],
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, name: '', rfids: [] }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (state.stage === 'name') {
      setState(prev => ({ ...prev, name: '', rfids: [] }));

      onClose();
    } else if (state.stage === 'rfids') {
      setState(prev => ({
        ...prev,
        stage: 'name',
        rfids: [],
      }));
    }
  }, [onClose, state]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'name' ? <ArrowBackRoundedIcon /> : undefined
      }
      header={
        state.stage !== 'name'
          ? ''
          : t('common:pages.rfid_groups.add_rfid_group.add_to_group')
      }
      hideCloseButton={state.stage === 'success'}
    >
      <ModalContentWrapper>
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          selectedRfids={selectedRfids}
        />
      </ModalContentWrapper>
    </Modal>
  );
};

export default AddDevicesToGroupsModal;
